/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * Estimate Notes Hook
 *
 * Handles fetching, creating, updating, and deleting notes for estimates.
 *
 * Following React Hooks best practices:
 * - React Query for caching and state management
 * - Proper TypeScript typing
 * - Organization-scoped queries
 */

"use client";

import { useAuthSafe } from "@/hooks/auth/use-auth-safe";
import { DEFAULT_PAGE_SIZE } from "@/lib/constants/query-config";
import { useOrganizationQueryGuard } from "@/hooks/use-organization-query";
import { createApiClient, isApiFailure } from "@/lib/api/api-client";
import { toastFromApiFailure } from "@/lib/api/toast-utils";
import type { Database } from "@/lib/supabase/types";
import type { PaginationInfo } from "@/lib/utils/responses";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { toast } from "sonner";
type EstimateNoteRow = Database["public"]["Tables"]["estimate_notes"]["Row"];

export interface UseEstimateNotesParams {
  estimateId: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  enabled?: boolean;
  staleTime?: number;
}

interface EstimateNotesQueryResult {
  notes: EstimateNoteRow[];
  pagination: PaginationInfo;
  total: number;
  pageCount: number;
}

export function useEstimateNotes(params: UseEstimateNotesParams) {
  const { getToken } = useAuthSafe();
  const queryClient = useQueryClient();

  const {
    estimateId,
    page = 1,
    limit = DEFAULT_PAGE_SIZE,
    sortBy = "created_at",
    sortOrder = "desc",
    enabled = true,
    staleTime = 2 * 60 * 1000,
  } = params;
  const { organizationId, queryEnabled, isOrganizationReady } =
    useOrganizationQueryGuard({
      enabled: enabled && !!estimateId,
      debugKey: "useEstimateNotes",
    });
  const ensureReady = () => {
    if (!organizationId || !isOrganizationReady) {
      throw new Error(
        "Still loading your organization. Give it a second and try again.",
      );
    }
  };

  const api = useMemo(
    () =>
      createApiClient({
        getToken,
        getOrganizationId: () => organizationId ?? undefined,
        autoRetry429: true,
      }),
    [getToken, organizationId],
  );

  const queryKey = useMemo(
    () => [
      "estimate-notes",
      organizationId,
      estimateId,
      { page, limit, sortBy, sortOrder },
    ],
    [organizationId, estimateId, page, limit, sortBy, sortOrder],
  );

  const {
    data: notesResult,
    isLoading,
    error,
    isError,
    refetch,
    isFetching,
  } = useQuery<EstimateNotesQueryResult>({
    queryKey,
    enabled: queryEnabled && !!estimateId,
    staleTime,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async (): Promise<EstimateNotesQueryResult> => {
      ensureReady();
      if (!estimateId) {
        throw new Error("Estimate ID is required");
      }

      const searchParams = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        sortBy,
        sortOrder,
      });

      const endpoint = `/api/estimates/${estimateId}/notes?${searchParams.toString()}`;
      const res = await api.get<EstimateNoteRow[]>(endpoint);

      if (isApiFailure(res)) {
        throw new Error(res.message);
      }

      const notesData = Array.isArray(res.data) ? res.data : [];

      const rawPagination = res.pagination;
      const total =
        typeof rawPagination?.total === "number"
          ? rawPagination.total
          : notesData.length;
      const pages =
        typeof rawPagination?.pages === "number"
          ? rawPagination.pages
          : Math.ceil(total / limit);

      const pagination: PaginationInfo = {
        page:
          typeof rawPagination?.page === "number" ? rawPagination.page : page,
        limit:
          typeof rawPagination?.limit === "number"
            ? rawPagination.limit
            : limit,
        total,
        pages,
        hasMore: page < pages,
        hasPrevious: page > 1,
      };

      return {
        notes: notesData,
        pagination,
        total,
        pageCount: pages,
      };
    },
  });

  const notes = notesResult?.notes ?? [];
  const pagination = notesResult?.pagination ?? {
    page,
    limit,
    total: 0,
    pages: 0,
    hasMore: false,
    hasPrevious: false,
  };

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey, exact: false });
  }, [queryClient, queryKey]);

  // Create note mutation
  const createNoteMutation = useMutation({
    mutationFn: async (data: { content: string }) => {
      ensureReady();
      if (!estimateId) throw new Error("Estimate ID is required");
      const res = await api.post<EstimateNoteRow>(
        `/api/estimates/${estimateId}/notes`,
        data,
      );
      if (isApiFailure(res)) {
        toastFromApiFailure("Failed to create note", res);
        throw new Error(res.message);
      }
      return res.data;
    },
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<EstimateNotesQueryResult>(queryKey);
      const optimistic: EstimateNoteRow = {
        note_id: `temp-${Date.now()}`,
        estimate_id: estimateId,
        content: data.content,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        created_by: null,
        updated_by: null,
        organization_id: organizationId ?? "",
      };
      queryClient.setQueryData<EstimateNotesQueryResult>(queryKey, (old) => ({
        notes: [optimistic, ...(old?.notes ?? [])],
        pagination: old?.pagination ?? pagination,
        total: (old?.total ?? 0) + 1,
        pageCount: old?.pageCount ?? 1,
      }));
      return { previous };
    },
    onError: (_err, _data, context) => {
      if (context?.previous)
        queryClient.setQueryData(queryKey, context.previous);
    },
    onSuccess: () => {
      toast.success("Note created");
    },
    onSettled: () => {
      invalidate();
    },
  });

  // Update note mutation
  const updateNoteMutation = useMutation({
    mutationFn: async ({
      noteId,
      data,
    }: {
      noteId: string;
      data: { content: string };
    }) => {
      ensureReady();
      if (!estimateId) throw new Error("Estimate ID is required");
      const res = await api.put<EstimateNoteRow>(
        `/api/estimates/${estimateId}/notes/${noteId}`,
        data,
      );
      if (isApiFailure(res)) {
        toastFromApiFailure("Failed to update note", res);
        throw new Error(res.message);
      }
      return res.data;
    },
    onMutate: async ({ noteId, data }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<EstimateNotesQueryResult>(queryKey);
      queryClient.setQueryData<EstimateNotesQueryResult>(queryKey, (old) => ({
        ...old!,
        notes: (old?.notes ?? []).map((n) =>
          n.note_id === noteId
            ? {
                ...n,
                content: data.content,
                updated_at: new Date().toISOString(),
              }
            : n,
        ),
      }));
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(queryKey, context.previous);
    },
    onSuccess: () => {
      toast.success("Note updated");
    },
    onSettled: () => {
      invalidate();
    },
  });

  // Delete note mutation
  const deleteNoteMutation = useMutation({
    mutationFn: async (noteId: string) => {
      ensureReady();
      if (!estimateId) throw new Error("Estimate ID is required");
      const res = await api.del(`/api/estimates/${estimateId}/notes/${noteId}`);
      if (isApiFailure(res)) {
        toastFromApiFailure("Failed to delete note", res);
        throw new Error(res.message);
      }
      return res.data;
    },
    onMutate: async (noteId) => {
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<EstimateNotesQueryResult>(queryKey);
      queryClient.setQueryData<EstimateNotesQueryResult>(queryKey, (old) => ({
        ...old!,
        notes: (old?.notes ?? []).filter((n) => n.note_id !== noteId),
        total: Math.max(0, (old?.total ?? 1) - 1),
      }));
      return { previous };
    },
    onError: (_err, _noteId, context) => {
      if (context?.previous)
        queryClient.setQueryData(queryKey, context.previous);
    },
    onSuccess: () => {
      toast.success("Note deleted");
    },
    onSettled: () => {
      invalidate();
    },
  });

  return {
    notes,
    pagination,
    total: pagination.total,
    pageCount: pagination.pages,
    isLoading,
    isFetching,
    isError,
    error: isError && error instanceof Error ? error.message : null,
    refetch,
    createNote: createNoteMutation.mutateAsync,
    updateNote: (noteId: string, data: { content: string }) =>
      updateNoteMutation.mutateAsync({ noteId, data }),
    deleteNote: deleteNoteMutation.mutateAsync,
    invalidate,
  };
}
